import React from 'react';
import { Link as RouterLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import { 
  Breadcrumbs as MuiBreadcrumbs, 
  Link, 
  Typography, 
  Box, 
  IconButton, 
  Tooltip 
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Folder as FolderIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Settings as SettingsIcon,
  Apps as AppsIcon,
  Add as AddIcon
} from '@mui/icons-material';

/**
 * Breadcrumbs Component
 * Displays navigation breadcrumbs with icons and back button
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectName - Project name for project breadcrumb
 * @param {string} props.documentName - Document name for document breadcrumb
 * @param {boolean} props.showBackButton - Whether to show the back button
 * @param {Function} props.onBackClick - Handler for back button click
 * @param {Object} props.customMappings - Custom path to label/icon mappings
 * @returns {React.ReactElement} Breadcrumbs component
 */
const Breadcrumbs = ({ 
  projectName, 
  documentName,
  showBackButton = true,
  onBackClick,
  customMappings = {},
  sx = {}
}) => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Handle back button click
  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  // Default path mappings
  const defaultPathMappings = {
    projects: { label: 'Projects', icon: <FolderIcon fontSize="small" /> },
    create: { label: 'New Project', icon: <AddIcon fontSize="small" /> },
    documents: { label: 'Documents', icon: <DescriptionIcon fontSize="small" /> },
    'prd-workflow': { label: 'PRD Workflow', icon: <AssignmentIcon fontSize="small" /> },
    questionnaire: { label: 'Questionnaire', icon: <AssignmentIcon fontSize="small" /> },
    conversation: { label: 'Conversation', icon: <QuestionAnswerIcon fontSize="small" /> },
    integrations: { label: 'Integrations', icon: <AppsIcon fontSize="small" /> },
    settings: { label: 'Settings', icon: <SettingsIcon fontSize="small" /> },
  };
  
  // Combine default and custom mappings
  const pathMappings = { ...defaultPathMappings, ...customMappings };

  // Helper to determine breadcrumb label and icon
  const getBreadcrumbInfo = (pathname, index) => {
    // Default values
    let label = pathname.charAt(0).toUpperCase() + pathname.slice(1);
    let icon = null;
    let to = `/${pathnames.slice(0, index + 1).join('/')}`;
    
    // Check if this path has a mapping
    if (pathMappings[pathname]) {
      label = pathMappings[pathname].label;
      icon = pathMappings[pathname].icon;
    }
    // For project IDs
    else if (index === 1 && pathname === params.projectId) {
      label = projectName || 'Project Details';
      icon = <FolderIcon fontSize="small" />;
    }
    // For document IDs
    else if (pathname === params.documentId) {
      label = documentName || 'Document';
      icon = <DescriptionIcon fontSize="small" />;
    }

    return { label, icon, to };
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2, 
        mt: 1,
        ...sx
      }}
    >
      {showBackButton && (
        <Tooltip title="Go Back">
          <IconButton 
            onClick={handleBack} 
            size="small" 
            sx={{ mr: 1, color: 'text.secondary' }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <MuiBreadcrumbs 
        separator="›" 
        aria-label="breadcrumb"
        sx={{ 
          '& .MuiBreadcrumbs-separator': { 
            mx: 1, 
            color: 'text.secondary' 
          } 
        }}
      >
        <Link 
          component={RouterLink} 
          to="/projects" 
          color="inherit" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
            textDecoration: 'none'
          }}
        >
          <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
          Home
        </Link>

        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const { label, icon, to } = getBreadcrumbInfo(value, index);

          return last ? (
            <Typography 
              key={to} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'text.primary',
                fontWeight: 500
              }}
            >
              {icon && <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{icon}</Box>}
              {label}
            </Typography>
          ) : (
            <Link
              component={RouterLink}
              to={to}
              key={to}
              color="inherit"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
                textDecoration: 'none'
              }}
            >
              {icon && <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>{icon}</Box>}
              {label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;